import { z } from 'zod';
import type { ContentI18n, SurveyExpiration, SurveyResponseType } from '@/shared/types/nps';
import { EXPIRATION_LABEL } from '../constants/survey';
import { compactContentI18n } from '../model/content-i18n';

const expirationValues = Object.keys(EXPIRATION_LABEL) as [SurveyExpiration, ...SurveyExpiration[]];

const localizedTextSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
});

const contentI18nSchema = z
  .object({
    'pt-PT': localizedTextSchema.optional(),
    en: localizedTextSchema.optional(),
    es: localizedTextSchema.optional(),
  })
  .optional();

export const surveyBaseSchema = z.object({
  title: z.string().min(1, 'Informe o título'),
  description: z.string().min(1, 'Informe a descrição'),
  i18n: contentI18nSchema,
  response_type: z.enum(['ANONYMOUS', 'IDENTIFIED']),
  /** Só enviado para IDENTIFIED — espelha o DTO do fixe-nps / api-v2. */
  response_expiration_time: z.enum(expirationValues).optional(),
  identification_required: z.boolean().default(false),
  answer_bonus: z.boolean().default(false),
});

export const createSurveySchema = surveyBaseSchema.transform((data) => {
  if (data.response_type === 'ANONYMOUS') {
    const { response_expiration_time: _exp, ...rest } = data;
    return { ...rest, identification_required: false, answer_bonus: false };
  }
  return data;
});

export const updateSurveySchema = surveyBaseSchema.partial();

export type CreateSurveyFormValues = z.infer<typeof createSurveySchema>;
export type UpdateSurveyFormValues = z.infer<typeof updateSurveySchema>;

export interface SurveyFormPayload {
  title: string;
  description?: string;
  i18n?: ContentI18n;
  response_type?: SurveyResponseType;
  response_expiration_time?: SurveyExpiration;
  identification_required?: boolean;
  answer_bonus?: boolean;
}

/** Monta o body compatível com a api-v2 (sem expiração em ANONYMOUS). */
export function toSurveyPayload(values: {
  title: string;
  description?: string;
  i18n?: ContentI18n | null;
  response_type?: SurveyResponseType;
  identification_required?: boolean;
  answer_bonus?: boolean;
  response_expiration_time?: SurveyExpiration;
}): SurveyFormPayload {
  const responseType = values.response_type ?? 'ANONYMOUS';
  const i18n =
    values.i18n !== undefined && values.i18n !== null
      ? (compactContentI18n(values.i18n) ?? {})
      : undefined;
  if (responseType === 'ANONYMOUS') {
    return {
      title: values.title,
      description: values.description,
      ...(i18n !== undefined ? { i18n } : {}),
      response_type: 'ANONYMOUS',
      identification_required: false,
      answer_bonus: false,
    };
  }
  return {
    title: values.title,
    description: values.description,
    ...(i18n !== undefined ? { i18n } : {}),
    response_type: 'IDENTIFIED',
    response_expiration_time: values.response_expiration_time,
    identification_required: values.identification_required ?? false,
    answer_bonus: values.answer_bonus ?? false,
  };
}
